import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

/**
 * Componente que redirige automáticamente al dashboard específico del restaurante
 * o al dashboard general si no tiene restaurantId
 */
const RestaurantRedirect = () => {
  const { currentUser, restaurantId, loading, needsOnboarding, customClaims, isCreatingGoogleProfile, logout, isSuperAdmin, hasOrg, isRestaurantSuspended } = useAuth();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Verificar flags temporales en sessionStorage
  const creatingGoogleProfile = sessionStorage.getItem('creatingGoogleProfile') === 'true';

  devLog('RestaurantRedirect - Estado:', {
    currentUser: currentUser?.email,
    restaurantId,
    loading,
    needsOnboarding,
    isCreatingGoogleProfile,
    creatingGoogleProfile,
  });

  // Mostrar loading mientras carga el estado O mientras los claims no están disponibles O mientras se crea perfil de Google
  if (loading || (currentUser && !customClaims) || isCreatingGoogleProfile || creatingGoogleProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3 flex items-center justify-center">
        <div className="kumia-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-text">
            <span className="text-content-primary">Kum</span>
            <span className="text-kumia-green">IA</span>
          </div>
        </div>
        {(isCreatingGoogleProfile || creatingGoogleProfile) && (
          <p className="text-content-muted mt-4 absolute bottom-20">Configurando tu cuenta...</p>
        )}
      </div>
    );
  }

  // Si no está autenticado, ir al login
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Si necesita onboarding, ir al onboarding
  if (needsOnboarding) {
    devLog('RestaurantRedirect: Redirigiendo a onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // Si el restaurante está suspendido (y no es super admin), mostrar página de suspensión
  if (isRestaurantSuspended) {
    devLog('RestaurantRedirect: Restaurante suspendido, redirigiendo a /suspended');
    return <Navigate to="/suspended" replace />;
  }

  // Si pertenece a una cadena (dueño o gerente), ir al selector de sedes.
  // Va antes del auto-ingreso a la sede primaria para que el dueño elija sede.
  if (hasOrg) {
    devLog('RestaurantRedirect: Miembro de cadena, redirigiendo al selector de sedes');
    return <Navigate to="/chain" replace />;
  }

  // Si es super admin sin restaurante seleccionado, ir al selector
  if (isSuperAdmin && !restaurantId) {
    devLog('RestaurantRedirect: Super admin sin restaurante, redirigiendo al selector');
    return <Navigate to="/admin/select-restaurant" replace />;
  }

  // Si tiene restaurantId, ir al dashboard específico
  if (restaurantId) {
    // Nueva estructura: /:restaurantId (sin /dashboard)
    return <Navigate to={`/${restaurantId}`} replace />;
  }

  // Si no hay restaurantId después de cargar, mostrar mensaje de error
  devLog('RestaurantRedirect - No se encontró restaurantId:', {
    currentUser: currentUser?.email,
    restaurantId
  });

  const handleLogoutAndRedirect = async () => {
    try {
      setIsLoggingOut(true);
      devLog('Cerrando sesión...');
      await logout();
      devLog('Sesión cerrada, redirigiendo al login...');
      // Usar navigate en lugar de window.location para transición suave
      navigate('/login', { replace: true });
    } catch (error) {
      devLog('Error al cerrar sesión:', error);
      setIsLoggingOut(false);
      // Si falla, usar navigate de todos modos
      navigate('/login', { replace: true });
    }
  };

  // Si está cerrando sesión, mostrar spinner
  if (isLoggingOut) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3 flex items-center justify-center">
        <div className="text-center">
          <div className="kumia-spinner mb-4">
            <div className="spinner-ring"></div>
            <div className="spinner-text">
              <span className="text-content-primary">Kum</span>
              <span className="text-kumia-green">IA</span>
            </div>
          </div>
          <p className="text-content-muted">Cerrando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h1 className="text-4xl font-bold text-content-primary mb-4">⚠️ Error de Configuración</h1>
        <p className="text-content-muted mb-4 text-lg">
          No se encontró un restaurante asociado a tu cuenta.
        </p>
        {/* <div className="bg-surface-3/50 rounded-lg p-4 mb-6 text-left">
          <p className="text-content-subtle text-sm mb-2">Información de debugging:</p>
          <ul className="text-xs text-content-muted space-y-1">
            <li>• Usuario: {currentUser?.email}</li>
            <li>• Restaurant ID: {restaurantId || 'No disponible'}</li>
            <li>• Custom Claims: {customClaims ? 'Cargados' : 'No cargados'}</li>
            <li>• Rol: {customClaims?.role || 'No disponible'}</li>
          </ul>
        </div> */}
        <p className="text-content-subtle text-md mb-6">
          Esta cuenta no tiene un restaurante asignado. Por favor, usa una cuenta de propietario (owner/admin) o contacta al soporte técnico.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-surface-5 text-content-primary font-semibold rounded-lg hover:bg-surface-5 transition-colors"
          >
            Reintentar
          </button>
          <button
            onClick={handleLogoutAndRedirect}
            className="px-6 py-3 bg-kumia-green text-black font-semibold rounded-lg hover:bg-green-500 transition-colors"
          >
            Volver al Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantRedirect;

